import $ from 'jquery';

import AlerterView from 'chairisher/view/alerter';
import ModalFactory from 'chairisher/factory/modal';
import TooltipHelper from 'chairisher/view/helper/tooltip';

import getRewardsRedemptionUrl from 'chairisher/context/reward';

import {
    trackRewardsDashboardLinkClick,
    trackRewardsRedemptionError,
    trackRewardsRedemptionLinkClick,
    trackRewardsRedemptionSuccess,
} from 'chairisher/analytics/reward';

/**
 * View that encapsulates behavior for the Reward dashboard.
 *
 * @param {jQuery} $el Instance of a jQuery element representing the dashboard
 */
class RewardDashboardView {
    constructor($el) {
        /**
         * Instance of a jQuery element representing the dashboard
         *
         * @type {jQuery}
         */
        this.$el = $el;

        /**
         * Selector used to find elements in the DOM that trigger the company info action
         *
         * @type {string}
         * @default
         */
        this.companyInfoActionSelector = '.js-trade-reward-company-info-action';

        /**
         * Selector used to select the trade reward company modal from the DOM
         *
         * @type {string}
         * @default
         */
        this.companyModalSelector = '#js-trade-reward-company-modal';

        /**
         * Instance of a jQuery element for the redemption modal
         *
         * @type {jQuery|null}
         */
        this.$modal = null;

        /**
         * Selector used to find elements in the DOM that trigger the redemption action
         *
         * @type {string}
         * @default
         */
        this.redemptionActionSelector = '.js-reward-redemption-action';

        /**
         * Id to use when creating a modal to redeem reward
         *
         * @type {string}
         * @default
         */
        this.rewardModalId = 'js-reward-redemption-modal';
    }

    /**
     * @returns {string} The reward program name
     */
    trackingProgramName() {
        return this.$el.data('program-name').toLowerCase();
    }

    /**
     * @returns {jQuery} The modal that houses various reward related screens
     */
    getModal(html) {
        if (!this.$modal) {
            this.$modal = ModalFactory.createModal(this.rewardModalId);
            this.$modal.find('.js-title').remove();

            $(document.body).append(this.$modal);

            this.$modal.on('submit', 'form', (e) => {
                e.preventDefault();
                const $form = $(e.currentTarget);
                this.submitRedemption({
                    url: $form.attr('action'),
                    method: $form.attr('method'),
                    data: $form.serialize(),
                });
            });

            this.$modal.on('click', '.js-close', (e) => {
                e.preventDefault();
                this.$modal.modal('hide');
            });
        }

        this.$modal.find('.js-modal-body').html(html);

        return this.$modal;
    }

    /**
     * Requests the Rewards redemption modal and populates this.$modal with its contents
     *
     * @returns {$.Deferred} Deferred to chain on when the request is complete
     */
    requestRedemptionForm() {
        return $.ajax({
            url: getRewardsRedemptionUrl(),
        })
            .done((data) => {
                this.getModal(data.html).modal();
            })
            .fail((jqXHR) => {
                if (jqXHR.responseJSON.errors) {
                    const { errors } = jqXHR.responseJSON;
                    for (let i = 0; i < errors.length; i += 1) {
                        AlerterView.error(errors[i].message);
                    }
                }
            });
    }

    /**
     * If the current user is a part of a company, this shows the modal listing all other company members
     */
    showCompanyModal() {
        $(this.companyModalSelector).modal('show');
    }

    /**
     * Submits a redemption request
     *
     * @param {Object} settings The settings object to pass into $.ajax()
     * @returns {$.Deferred} Deferred to chain on when the request is complete
     */
    submitRedemption(settings) {
        return $.ajax(settings)
            .done((data) => {
                this.getModal(data.html).modal();

                if (data.is_redemption_complete) {
                    this.$el.find('.js-reward-redemption').html('');
                    trackRewardsRedemptionSuccess(this.trackingProgramName());
                }
            })
            .fail((jqXHR) => {
                if (jqXHR.responseJSON.errors) {
                    const { errors } = jqXHR.responseJSON;
                    for (let i = 0; i < errors.length; i += 1) {
                        AlerterView.error(errors[i].message);
                    }
                    trackRewardsRedemptionError(this.trackingProgramName());
                }
            });
    }

    /**
     * Binds various actions to the view's elements
     */
    bind() {
        //
        // Display Company Modal
        //

        $(this.companyInfoActionSelector).on('click', (e) => {
            e.preventDefault();
            this.showCompanyModal();
            trackRewardsDashboardLinkClick(e, this.trackingProgramName());
        });

        //
        // Rewards Redemption
        //

        $(this.redemptionActionSelector).on('click', (e) => {
            e.preventDefault();
            this.requestRedemptionForm();
            trackRewardsRedemptionLinkClick(e, this.trackingProgramName());
        });

        //
        // Rewards Tooltip
        //

        const $rewardTooltip = $('.js-reward-tooltip');
        $rewardTooltip.tooltip({ placement: TooltipHelper.getPlacement });
    }
}

export default RewardDashboardView;
